import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronRight, MessageCircle, Mail } from "lucide-react";
import { useLang, COMPANY } from "@/lib/i18n";
import { scrollToId } from "@/lib/ui";

const NAV = [
  { id: "acasa", ro: "Acasă", en: "Home" },
  { id: "despre", ro: "Despre", en: "About" },
  { id: "servicii", ro: "Servicii", en: "Services" },
  { id: "flota", ro: "Flotă", en: "Fleet" },
  { id: "proces", ro: "Proces", en: "Process" },
  { id: "acoperire", ro: "Acoperire", en: "Coverage" },
  { id: "contact", ro: "Contact", en: "Contact" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const wasOpen = open;
    setOpen(false);
    const run = () => {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(id), 150);
      } else {
        scrollToId(id);
      }
    };
    if (wasOpen) {
      // let the mobile menu collapse before measuring/scrolling
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      run();
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-lg shadow-slate-900/10" : ""
      }`}
    >
      <div className="mx-auto max-w-7xl">
        <div className="px-5 lg:px-8 h-[72px] flex items-center justify-between">
          <button data-testid="logo-home" onClick={() => go("acasa")} className="flex items-center group shrink-0">
            <img src={COMPANY.logo} alt="HEB TEAM TRANS SRL" className="h-9 w-auto transition-transform group-hover:-translate-y-0.5" />
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => go(n.id)}
                className="text-sm font-semibold text-slate-600 hover:text-slate-950 transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 hover:after:w-full after:bg-blue-600 after:transition-all"
              >
                {t(n)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <div className="hidden sm:flex items-center rounded-full bg-slate-100 ring-1 ring-slate-200 p-0.5 text-xs font-bold">
              {["ro", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    lang === l ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              data-testid="header-cta-quote"
              onClick={() => go("contact")}
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
            >
              <Phone className="h-4 w-4" />
              {t({ ro: "Cere ofertă", en: "Request quote" })}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full bg-slate-100 ring-1 ring-slate-200 text-slate-900"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden border-t border-slate-100"
            >
              <div className="px-4 py-4 flex flex-col gap-1.5">
                {NAV.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`mnav-${n.id}`}
                    onClick={() => go(n.id)}
                    className="group flex items-center justify-between rounded-xl px-4 py-3 text-slate-800 font-semibold hover:bg-blue-600 hover:text-white active:scale-[0.99] transition-colors"
                  >
                    {t(n)}
                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-transform" />
                  </button>
                ))}

                <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-4">
                  <div className="flex items-center gap-2">
                    {["ro", "en"].map((l) => (
                      <button
                        key={l}
                        onClick={() => setLang(l)}
                        className={`px-4 py-2 rounded-full text-xs font-bold ${lang === l ? "bg-blue-600 text-white" : "bg-slate-100 ring-1 ring-slate-200 text-slate-700"}`}
                      >
                        {l.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`tel:${COMPANY.phoneRaw}`} className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-700 hover:bg-blue-600 hover:text-white transition-colors"><Phone className="h-4 w-4" /></a>
                    <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-700 hover:bg-green-600 hover:text-white transition-colors"><MessageCircle className="h-4 w-4" /></a>
                    <a href={`mailto:${COMPANY.email}`} className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-700 hover:bg-blue-600 hover:text-white transition-colors"><Mail className="h-4 w-4" /></a>
                  </div>
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
